import { useState } from "react";
import { PageBanner } from "@/shared/components/PageBanner";
import {
  useAddRosterDay,
  useCreateRoster,
  useCreateShiftTemplate,
  useRosters,
  useShiftTemplates,
} from "../api";
import type { ShiftTemplateCategory, Roster, ShiftTemplate, RosterDay } from "../types";

const CATEGORY_LABELS: Record<ShiftTemplateCategory, string> = {
  normal: "عادی",
  overtime_before: "اضافه‌کاری قبل از شیفت",
  overtime_after: "اضافه‌کاری بعد از شیفت",
  rest: "استراحت/تعطیلی",
  holiday: "تعطیل رسمی",
  leave: "مرخصی",
  mission: "مأموریت",
};

export default function RosterManagerPage() {
  const { data: templates = [] } = useShiftTemplates();
  const { data: rosters = [] } = useRosters();
  const createTemplate = useCreateShiftTemplate();
  const createRoster = useCreateRoster();
  const addRosterDay = useAddRosterDay();

  const [newTemplate, setNewTemplate] = useState({
    code: "",
    name: "",
    category: "normal" as ShiftTemplateCategory,
    start_time: "",
    end_time: "",
  });
  const [newRoster, setNewRoster] = useState({ code: "", name: "", cycle_length_days: 14 });
  const [selectedRosterId, setSelectedRosterId] = useState<string | null>(null);
  const [dayAssignment, setDayAssignment] = useState<{ day_index: number; shift_template: string }>({
    day_index: 1,
    shift_template: "",
  });

  const selectedRoster = rosters.find((r: Roster) => r.id === selectedRosterId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", fontFamily: "var(--font-family-fa, sans-serif)" }} dir="rtl">
      <PageBanner
        title="مدیریت گروه‌های کاری (Rosters)"
        subtitle="ایجاد و مدیریت الگوهای چرخشی یا ثابت تخصیص شیفت‌ها به صورت دوره‌ای"
        icon="📅"
        helpLink="/help/shopfloor/rosters"
      />

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>الگوهای شیفت (ShiftTemplate)</h2>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد الگو</label>
            <input
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="مثلاً MORN"
              value={newTemplate.code}
              onChange={(e) => setNewTemplate((s) => ({ ...s, code: e.target.value }))}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نام الگو</label>
            <input
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="نام"
              value={newTemplate.name}
              onChange={(e) => setNewTemplate((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>دسته‌بندی</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={newTemplate.category}
              onChange={(e) => setNewTemplate((s) => ({ ...s, category: e.target.value as ShiftTemplateCategory }))}
            >
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {newTemplate.category !== "rest" && newTemplate.category !== "holiday" && (
            <>
              <div style={{ flex: "1 1 150px" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ساعت شروع</label>
                <input
                  type="time"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                  value={newTemplate.start_time}
                  onChange={(e) => setNewTemplate((s) => ({ ...s, start_time: e.target.value }))}
                />
              </div>
              <div style={{ flex: "1 1 150px" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>ساعت پایان</label>
                <input
                  type="time"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                  value={newTemplate.end_time}
                  onChange={(e) => setNewTemplate((s) => ({ ...s, end_time: e.target.value }))}
                />
              </div>
            </>
          )}
          <div style={{ flex: "1 1 100%", display: "flex", justifyContent: "flex-end" }}>
            <button style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: createTemplate.isPending ? "not-allowed" : "pointer", opacity: createTemplate.isPending ? 0.7 : 1 }} onClick={() => createTemplate.mutate(newTemplate)} disabled={createTemplate.isPending}>
              افزودن الگو
            </button>
          </div>
        </div>
        
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#2B2B2B", marginBottom: "1rem" }}>الگوهای ثبت‌شده</h3>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {templates.map((t: ShiftTemplate) => (
              <li key={t.id} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "10px 12px", borderRadius: "8px", background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.05)", fontSize: "0.85rem" }}>
                <span style={{ fontWeight: 800, color: "#007270", minWidth: "60px" }}>{t.code}</span>
                <span style={{ fontWeight: 600, color: "#444", flex: 1 }}>{t.name} <span style={{ color: "#888", fontWeight: 400 }}>({CATEGORY_LABELS[t.category as keyof typeof CATEGORY_LABELS]})</span></span>
                {t.start_time && <span style={{ color: "#444", fontWeight: 600, background: "#FFF", padding: "2px 8px", borderRadius: "4px", border: "1px solid #EEE" }} dir="ltr">{t.start_time} - {t.end_time}</span>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>گروه‌های کاری / دوره‌های چرخشی (Roster)</h2>
        </div>
        
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "flex-end", marginBottom: "2rem", paddingBottom: "2rem", borderBottom: "1px dashed var(--color-border, #E4DCC8)" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد</label>
            <input
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="کد Roster"
              value={newRoster.code}
              onChange={(e) => setNewRoster((s) => ({ ...s, code: e.target.value }))}
            />
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نام</label>
            <input
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="نام Roster"
              value={newRoster.name}
              onChange={(e) => setNewRoster((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>طول دوره (روز)</label>
            <input
              type="number"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              value={newRoster.cycle_length_days}
              onChange={(e) => setNewRoster((s) => ({ ...s, cycle_length_days: Number(e.target.value) }))}
            />
          </div>
          <div style={{ flex: "1 1 100%", display: "flex", justifyContent: "flex-end" }}>
            <button style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: createRoster.isPending ? "not-allowed" : "pointer", opacity: createRoster.isPending ? 0.7 : 1 }} onClick={() => createRoster.mutate(newRoster)} disabled={createRoster.isPending}>
              ایجاد Roster
            </button>
          </div>
        </div>

        <div>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "8px" }}>انتخاب Roster برای تعریف روزهای چرخه</label>
          <select style={{ width: "100%", maxWidth: "400px", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer", marginBottom: "1.5rem" }} value={selectedRosterId ?? ""} onChange={(e) => setSelectedRosterId(e.target.value || null)}>
            <option value="">— انتخاب کنید —</option>
            {rosters.map((r: Roster) => (
              <option key={r.id} value={r.id}>
                {r.code} ({r.cycle_length_days} روزه)
              </option>
            ))}
          </select>

          {selectedRoster && (
            <div style={{ background: "rgba(0,0,0,0.02)", border: "1px solid var(--color-border, #E4DCC8)", borderRadius: "10px", padding: "1.5rem" }}>
              <h3 style={{ fontSize: "1rem", fontWeight: 800, color: "#2B2B2B", marginBottom: "1rem", borderBottom: "1px solid rgba(0,0,0,0.05)", paddingBottom: "0.5rem" }}>تقویم {selectedRoster.cycle_length_days} روزهٔ چرخه</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))", gap: "8px", marginBottom: "1.5rem" }}>
                {Array.from({ length: selectedRoster.cycle_length_days }, (_, i) => i + 1).map((dayIndex) => {
                  const existing = selectedRoster.days.find((d: RosterDay) => d.day_index === dayIndex);
                  return (
                    <div key={dayIndex} style={{ border: `1px solid ${existing ? "rgba(16, 185, 129, 0.4)" : "var(--color-border, #E4DCC8)"}`, background: existing ? "rgba(16, 185, 129, 0.05)" : "#FFF", borderRadius: "8px", padding: "8px 4px", textAlign: "center", fontSize: "0.75rem", display: "flex", flexDirection: "column", gap: "4px" }}>
                      <div style={{ fontWeight: 700, color: "#888" }}>روز {dayIndex}</div>
                      <div style={{ fontWeight: 800, color: existing ? "#059669" : "#CCC" }}>{existing ? existing.shift_template_display.code : "—"}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end", background: "#FFF", padding: "1rem", borderRadius: "8px", border: "1px solid rgba(0,0,0,0.05)" }}>
                <div>
                  <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>روز شماره</label>
                  <input
                    type="number"
                    style={{ width: "80px", padding: "8px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                    min={1}
                    max={selectedRoster.cycle_length_days}
                    value={dayAssignment.day_index}
                    onChange={(e) => setDayAssignment((s) => ({ ...s, day_index: Number(e.target.value) }))}
                  />
                </div>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>الگوی شیفت</label>
                  <select
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
                    value={dayAssignment.shift_template}
                    onChange={(e) => setDayAssignment((s) => ({ ...s, shift_template: e.target.value }))}
                  >
                    <option value="">— انتخاب الگو —</option>
                    {templates.map((t: ShiftTemplate) => (
                      <option key={t.id} value={t.id}>
                        {t.code} ({t.name})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  style={{ padding: "8px 16px", borderRadius: "8px", background: "#10B981", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: !dayAssignment.shift_template || addRosterDay.isPending ? "not-allowed" : "pointer", opacity: !dayAssignment.shift_template || addRosterDay.isPending ? 0.5 : 1 }}
                  onClick={() => addRosterDay.mutate({ roster: selectedRoster.id, ...dayAssignment })}
                  disabled={!dayAssignment.shift_template || addRosterDay.isPending}
                >
                  ثبت الگو برای این روز
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
